import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import LabelWrap from 'components/inputs/LabelWrap';
import CompanyPicker from 'components/CompanyPicker';
import Select from 'components/inputs/Select';
import Input from 'components/inputs/Input';
import AutoSizedTextArea from 'components/inputs/AutoSizedTextArea';
import {
  formatTime,
  getHoursAndMinutesFromMillisecondsString,
  toInputDateTimeString,
} from 'utils/date-helpers';
import {
  AssignmentModel,
  AssignmentRequestModel,
  AssignmentRequestReasonEnum,
} from 'api/model';
import ConfirmModal from 'components/Modal/ConfirmModal';
import { useForm } from 'react-hook-form';
import useAssignmentRequestReasonSelectOptions from 'pages/InternalDeliveryGroup/Assignments/components/useAssignmentRequestReasonOptions';
import useCompanies from 'contexts/basicData/useCompanies';
import toast from 'react-hot-toast';
import KeyValueList from 'components/KeyValueList';
import { useAssignmentRequestsCreateAssignmentRequest } from 'api/assignment-requests/assignment-requests';
import FacilityLockWarning, {
  useToCompanyLock,
} from 'components/AssignmentRequests/FacilityLockWarning';

const FIXED_PADDING_TIME_IN_MILLISECONDS = 30 * 60 * 1000;

const Horizontal = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
`;

const ContentWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 400px;
  width: 100%;
  margin: 0 auto;
  overflow: hidden;
  overflow-y: auto;
`;

const Bold = styled.div`
  font-weight: 500;
`;

const ExtraInfoInput = styled(AutoSizedTextArea)`
  resize: vertical;
  min-height: 75px;
  width: 100%;
`;

interface Props {
  forUserId: number;
  reasonInfo?: React.ReactNode;
  /** Förvald anledning: början för tillresa till ett uppdrag, slut för hemresan. */
  initialReason: AssignmentRequestReasonEnum;
  initialToCompanyId?: number;
  lastPossibleArrivalTime?: Date;
  earliestPossibleStartTime?: Date;
  onBehalfOfAssignment?: AssignmentModel;

  onChange: (change: AssignmentRequestModel) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

const CreateAssignmentRequestModal: React.FC<Props> = ({
  reasonInfo,
  initialReason,
  initialToCompanyId,
  lastPossibleArrivalTime,
  earliestPossibleStartTime,
  forUserId,
  onBehalfOfAssignment,
  onChange,
  onCancel,
  onConfirm,
}) => {
  const createAssignmentRequestCall =
    useAssignmentRequestsCreateAssignmentRequest();

  const { companies, companiesById } = useCompanies();

  const firstLogisticsCompany = companies.find(
    (c) => c.isLogisticsCenter && c.name === 'Logistik - Strängnäs'
  ); // lock to strängnäs logistik for now, until we open up more locations

  const assignmentRequestReasonSelectOptions =
    useAssignmentRequestReasonSelectOptions();

  const assignmentRequestForm = useForm<AssignmentRequestModel>({
    defaultValues: {
      fromCompanyID: firstLogisticsCompany?.companyID,
      toCompanyID: initialToCompanyId,
      requestedForID: forUserId,
      targetStartTime: earliestPossibleStartTime,
      assignmentRequestReasonID: initialReason,
      originatingAssignmentID: onBehalfOfAssignment?.assignmentID,
      originatingCaseID: onBehalfOfAssignment?.caseID,
    },
  });

  const handleConfirm = async (assignmentRequest: AssignmentRequestModel) => {
    try {
      await createAssignmentRequestCall.mutateAsync({
        data: assignmentRequest,
      });
      toast.success('Annonsförfrågan skapad.');
      onConfirm();
    } catch (error) {
      toast.error('Något gick fel när annonsförfrågan skulle skapas.');
    }
  };

  useEffect(() => {
    if (firstLogisticsCompany) {
      assignmentRequestForm.setValue(
        'fromCompanyID',
        firstLogisticsCompany.companyID
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstLogisticsCompany]);

  const {
    fromCompanyID,
    toCompanyID,
    targetStartTime,
    extraInfo,
    assignmentRequestReasonID,
  } = assignmentRequestForm.watch();

  useEffect(() => {
    // When the form values change, we need to call the onChange prop with the updated values
    const updatedAssignmentRequest: AssignmentRequestModel = {
      fromCompanyID,
      toCompanyID,
      targetStartTime,
      extraInfo,
      assignmentRequestReasonID,
    } as AssignmentRequestModel;
    onChange(updatedAssignmentRequest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fromCompanyID,
    toCompanyID,
    targetStartTime,
    extraInfo,
    assignmentRequestReasonID,
  ]);

  // // calculated time to get to logistik based on when the user wants to pick up the car
  // const derivedTimeToGetToLogistik = useMemo(() => {

  const fixedAssignmentTimeInMilliseconds = useMemo(() => {
    if (fromCompanyID === undefined || toCompanyID === undefined) {
      return 0;
    }
    const logistikToCompanyPrice = companiesById
      .get(fromCompanyID)
      ?.pricesFromHere.find((p) => p.toCompanyId === toCompanyID);
    return (
      logistikToCompanyPrice?.fixedTimeInMilliseconds ??
      FIXED_PADDING_TIME_IN_MILLISECONDS
    );
  }, [companiesById, fromCompanyID, toCompanyID]);

  // when the fixedAssignmentTimeInMilliseconds, we need to update the targetStartTime based on the new company and the information we have
  useEffect(() => {
    if (fixedAssignmentTimeInMilliseconds) {
      // if we have an earliest possible start time, we don't change anything, as the fixed time does not affect it
      // if we have a last possible arrival time, we need to set the target start time to the last possible arrival time - the fixed time
      if (lastPossibleArrivalTime) {
        const newTargetStartTime = new Date(
          lastPossibleArrivalTime!.getTime() - fixedAssignmentTimeInMilliseconds
        );
        assignmentRequestForm.setValue('targetStartTime', newTargetStartTime);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedAssignmentTimeInMilliseconds]);

  const derivedArrivalTime = useMemo(() => {
    if (targetStartTime) {
      return new Date(
        targetStartTime.getTime() + fixedAssignmentTimeInMilliseconds
      );
    }
    return undefined;
  }, [fixedAssignmentTimeInMilliseconds, targetStartTime]);

  const availableTimeBetweenStartAndEnd = useMemo(() => {
    if (lastPossibleArrivalTime && earliestPossibleStartTime) {
      return (
        lastPossibleArrivalTime.getTime() - earliestPossibleStartTime.getTime()
      );
    }
    return undefined;
  }, [lastPossibleArrivalTime, earliestPossibleStartTime]);

  const toCompanyLock = useToCompanyLock(toCompanyID, targetStartTime);

  const disableConfirm =
    // till-anläggningen är låst i annonsöverblicken (backenden nekar ändå)
    toCompanyLock.isLocked ||
    // the fixed assignment time is greater than the available time between start and end
    (availableTimeBetweenStartAndEnd &&
      availableTimeBetweenStartAndEnd < fixedAssignmentTimeInMilliseconds) ||
    // the derived arrival time is after the last possible arrival time
    (lastPossibleArrivalTime &&
      derivedArrivalTime &&
      derivedArrivalTime.getTime() > lastPossibleArrivalTime.getTime()) ||
    // the target start time is before the earliest possible start time
    (earliestPossibleStartTime &&
      targetStartTime &&
      targetStartTime.getTime() < earliestPossibleStartTime.getTime());

  return (
    <ConfirmModal
      onConfirm={() => {
        assignmentRequestForm.handleSubmit(handleConfirm)();
      }}
      confirmText={
        createAssignmentRequestCall.isLoading
          ? 'Skickar förfrågan...'
          : 'Skicka förfrågan'
      }
      onCancel={onCancel}
      title="Annonsförfrågan"
      disabled={disableConfirm || createAssignmentRequestCall.isLoading}
    >
      <ContentWrap>
        {reasonInfo}
        <KeyValueList
          colonKey
          rows={[
            {
              key: 'Tillgänglig tid',
              value: (
                <div>
                  {availableTimeBetweenStartAndEnd &&
                    getHoursAndMinutesFromMillisecondsString(
                      availableTimeBetweenStartAndEnd
                    )}
                </div>
              ),
            },
          ]}
        />
        <LabelWrap label={<Bold>Från:</Bold>}>
          <CompanyPicker
            filter={(company) => !!company.isLogisticsCenter}
            disabled
            selectedCompanyId={fromCompanyID}
            onCompanySelected={() => {
              // Do nothing, this is a logistics center
            }}
          />
        </LabelWrap>

        <LabelWrap label={<Bold>Till:</Bold>}>
          <Horizontal>
            <CompanyPicker
              filter={(company) =>
                company.showInInternalDeliveryOverview ||
                company.isZakraHub === true
              }
              onCompanySelected={(newValue) => {
                if (newValue) {
                  assignmentRequestForm.setValue(
                    'toCompanyID',
                    newValue?.companyID
                  );
                }
              }}
              selectedCompanyId={toCompanyID}
            />
            <FacilityLockWarning lock={toCompanyLock.lock} />
          </Horizontal>
        </LabelWrap>
        <LabelWrap
          label={<Bold>Fast tid:</Bold>}
          errorLabel={
            availableTimeBetweenStartAndEnd &&
            availableTimeBetweenStartAndEnd <
              fixedAssignmentTimeInMilliseconds &&
            `Den fasta tiden (${getHoursAndMinutesFromMillisecondsString(
              fixedAssignmentTimeInMilliseconds
            )}) överstiger den tillgängliga tiden (${getHoursAndMinutesFromMillisecondsString(
              availableTimeBetweenStartAndEnd
            )})`
          }
        >
          <div>
            {getHoursAndMinutesFromMillisecondsString(
              fixedAssignmentTimeInMilliseconds ?? 0
            )}
          </div>
        </LabelWrap>
        <LabelWrap label={<Bold>Anledning:</Bold>}>
          <Select
            value={assignmentRequestReasonID}
            onChange={(eve) => {
              assignmentRequestForm.setValue(
                'assignmentRequestReasonID',
                Number(eve.currentTarget.value) as AssignmentRequestReasonEnum
              );
            }}
          >
            {assignmentRequestReasonSelectOptions.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
        </LabelWrap>

        <LabelWrap label={<Bold>När vill du hämta ut annonsbilen?</Bold>}>
          <Horizontal>
            <Input
              max={
                lastPossibleArrivalTime
                  ? toInputDateTimeString(lastPossibleArrivalTime)
                  : undefined
              }
              min={
                earliestPossibleStartTime
                  ? toInputDateTimeString(earliestPossibleStartTime)
                  : undefined
              }
              onChange={(e) => {
                const newValue = new Date(e.currentTarget.value);
                if (lastPossibleArrivalTime) {
                  const resultingArrivalTime =
                    newValue.getTime() + fixedAssignmentTimeInMilliseconds;
                  if (
                    resultingArrivalTime < lastPossibleArrivalTime.getTime()
                  ) {
                    assignmentRequestForm.setValue('targetStartTime', newValue);
                  } else {
                    toast.error(
                      `Du kan inte välja en tid som resulterar i ankomst senare än den sista möjliga tiden (${formatTime(lastPossibleArrivalTime)}). ${formatTime(newValue)} + ${getHoursAndMinutesFromMillisecondsString(
                        fixedAssignmentTimeInMilliseconds
                      )} = ${formatTime(
                        new Date(
                          newValue.getTime() + fixedAssignmentTimeInMilliseconds
                        )
                      )}`
                    );
                  }
                } else if (earliestPossibleStartTime) {
                  if (
                    newValue.getTime() > earliestPossibleStartTime.getTime()
                  ) {
                    assignmentRequestForm.setValue('targetStartTime', newValue);
                  } else {
                    toast.error(
                      `Du kan inte välja en tid som är tidigare än den tidigaste möjliga tiden (${formatTime(earliestPossibleStartTime)}).`
                    );
                  }
                } else {
                  assignmentRequestForm.setValue('targetStartTime', newValue);
                }
              }}
              small
              type="datetime-local"
              value={toInputDateTimeString(targetStartTime)}
            />
            {derivedArrivalTime && (
              <div>
                Beräknad ankomst vid anläggning:{' '}
                {formatTime(derivedArrivalTime)}
                {` (${getHoursAndMinutesFromMillisecondsString(
                  fixedAssignmentTimeInMilliseconds
                )})`}
              </div>
            )}
          </Horizontal>
        </LabelWrap>
        {/* <KeyValueList
            colonKey
            rows={[
              {
                key: 'Tid att ta sig till Logistik',
                value: (
                  <div>
                    {getHoursAndMinutesFromMillisecondsString(
                      derivedTimeToGetToLogistik
                    )}
                  </div>
                ),
              },
            ]}
          /> */}
        <LabelWrap label={<Bold>Information till annonsgruppen:</Bold>}>
          <ExtraInfoInput
            onChange={(e) => {
              assignmentRequestForm.setValue(
                'extraInfo',
                e.currentTarget.value
              );
            }}
            placeholder="Lägg in eventuell information till annonsgruppen här"
            value={extraInfo}
          />
        </LabelWrap>
      </ContentWrap>
    </ConfirmModal>
  );
};

export default CreateAssignmentRequestModal;
